'use client';

import { ChevronDownIcon } from 'lucide-react';
import {
  Root,
  Item,
  Header,
  Content,
  Trigger,
} from '@radix-ui/react-accordion';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export function Accordion(props: ComponentProps<typeof Root>): JSX.Element {
  return <Root data-slot='accordion' {...props} />;
}

export function AccordionItem({
  className,
  ...props
}: ComponentProps<typeof Item>): JSX.Element {
  return (
    <Item
      data-slot='accordion-item'
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

export function AccordionTrigger({
  children,
  className,
  ...props
}: ComponentProps<typeof Trigger>): JSX.Element {
  return (
    <Header className='flex'>
      <Trigger
        data-slot='accordion-trigger'
        className={cn(
          'focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className='text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200' />
      </Trigger>
    </Header>
  );
}

export function AccordionContent({
  children,
  className,
  ...props
}: ComponentProps<typeof Content>): JSX.Element {
  return (
    <Content
      data-slot='accordion-content'
      className='data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm'
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </Content>
  );
}
